import React, { useState, useEffect, useCallback } from 'react';
import DocumentViewer from '../components/DocumentViewer';

const scoreColor = s => s >= 4?'#2E7D32':s >= 3?'#1565C0':s >= 2?'#F57F17':'#C62828';

const STATUS_COLORS = {
  Queued:    { bg:'#F5F5F5', fg:'#7A8899' },
  Pending:   { bg:'#F5F5F5', fg:'#7A8899' },
  Running:   { bg:'#FFF3E0', fg:'#E65100' },
  Processing:{ bg:'#FFF3E0', fg:'#E65100' },
  Completed: { bg:'#E8F5E9', fg:'#2E7D32' },
  Failed:    { bg:'#FFEBEE', fg:'#C62828' },
  Blocked:   { bg:'#FFEBEE', fg:'#C62828' },
};

function formatSize(bytes) {
  if (bytes >= 1048576) return (bytes / 1048576).toFixed(1) + ' MB';
  return (bytes / 1024).toFixed(0) + ' KB';
}

function formatDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('en-GB', { day:'numeric',month:'short',year:'numeric' })
    + ', ' + d.toLocaleTimeString('en-GB', { hour:'2-digit',minute:'2-digit' });
}

export default function DocumentUploads({ API, engagement }) {
  const [controls, setControls] = useState([]);
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dragging, setDragging] = useState(false);
  const [uploadFor, setUploadFor] = useState(null);
  const [filterDomain, setFilterDomain] = useState('all');
  const [expanded, setExpanded] = useState(null);
  const [viewerDoc, setViewerDoc] = useState(null);
  const [overrideOpen, setOverrideOpen] = useState(null);
  const [overrideScore, setOverrideScore] = useState(null);
  const [overrideReason, setOverrideReason] = useState('');
  const [overview, setOverview] = useState(null);

  const assessor = engagement?.assessor || 'Assessor';

  const loadData = useCallback(async () => {
    try {
      const [ctrlRes, docRes, ovRes] = await Promise.all([
        fetch(`${API}/api/documents/controls`),
        fetch(`${API}/api/documents`),
        fetch(`${API}/api/documents/overview`),
      ]);
      const [ctrlData, docData, ovData] = await Promise.all([
        ctrlRes.json(), docRes.json(), ovRes.json(),
      ]);
      setControls(ctrlData);
      setDocs(docData);
      setOverview(ovData);
    } catch (e) {
      console.error('Failed to load document data:', e);
    }
    setLoading(false);
  }, [API]);

  useEffect(() => { loadData(); }, [loadData]);

  // Poll while any doc is processing
  useEffect(() => {
    const hasProcessing = docs.some(d =>
      ['Queued','Pending','Running','Processing'].includes(d.processing_status));
    if (!hasProcessing) return;
    const timer = setInterval(loadData, 10000);
    return () => clearInterval(timer);
  }, [docs, loadData]);

  const handleUpload = async (files, controlLinks) => {
    for (const file of files) {
      const formData = new FormData();
      formData.append('File', file);
      if (controlLinks?.length > 0) {
        controlLinks.forEach((link, i) => {
          formData.append(`ControlLinks[${i}].ControlDefinitionId`, link.controlDefinitionId || '00000000-0000-0000-0000-000000000000');
          formData.append(`ControlLinks[${i}].ControlId`, link.controlId);
          formData.append(`ControlLinks[${i}].IsPrimary`, link.isPrimary ?? true);
        });
      }
      try {
        await fetch(`${API}/api/documents/upload`, { method:'POST', body: formData });
      } catch (e) {
        console.error('Upload failed:', e);
      }
    }
    setUploadFor(null);
    loadData();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) handleUpload(files, []);
  };

  const handleConfirm = async (controlId) => {
    await fetch(`${API}/api/controls/${controlId}/confirm`, { method:'POST' });
    loadData();
  };

  const handleOverride = async (controlId) => {
    if (!overrideScore) return;
    await fetch(`${API}/api/controls/${controlId}`, {
      method:'PUT',
      headers:{'Content-Type':'application/json'},
      body: JSON.stringify({
        score: overrideScore,
        assessor_notes: overrideReason,
        score_source: 'assessor_override',
        override_reason: overrideReason,
      }),
    });
    setOverrideOpen(null);
    setOverrideScore(null);
    setOverrideReason('');
    loadData();
  };

  const coveredIds = docs.flatMap(d => d.mapped_controls || []);
  const needsReviewCount = docs.filter(d =>
    d.analysis?.status === 'Completed' && d.analysis.results?.some(r => r.score <= 2)).length;

  const checklist = controls.map(c => {
    const doc = docs.find(d => (d.mapped_controls || []).includes(c.id));
    let st = 'missing';
    if (doc) {
      const ps = doc.processing_status;
      if (ps === 'Completed' && doc.analysis?.status === 'Completed') st = 'completed';
      else if (['Queued','Pending','Running','Processing'].includes(ps)) st = 'processing';
      else if (ps === 'Failed' || ps === 'Blocked') st = 'failed';
      else st = 'uploaded';
    }
    return { ...c, doc, controlStatus: st };
  });

  const filtered = filterDomain === 'all' ? checklist : checklist.filter(c => c.domain === filterDomain);
  const uniqueDomains = [...new Set(controls.map(c => c.domain))].sort();

  if (loading) {
    return <div style={{textAlign:'center',padding:40,color:'#7A8899'}}>Loading documents...</div>;
  }

  return (
    <div>
      {/* Overview stats */}
      {overview && (
        <div style={{display:'grid',gridTemplateColumns:'repeat(5,1fr)',gap:10,marginBottom:16}}>
          <div className="stat-card" style={{padding:'10px 14px'}}>
            <div style={{fontSize:22,fontWeight:700,color:'#1565C0',lineHeight:1}}>{docs.length}</div>
            <div style={{fontSize:10,color:'#7A8899',marginTop:2}}>Documents uploaded</div>
          </div>
          <div className="stat-card" style={{padding:'10px 14px'}}>
            <div style={{fontSize:22,fontWeight:700,color:'#2E7D32',lineHeight:1}}>{overview.completed}</div>
            <div style={{fontSize:10,color:'#7A8899',marginTop:2}}>AI-scored</div>
          </div>
          <div className="stat-card" style={{padding:'10px 14px'}}>
            <div style={{fontSize:22,fontWeight:700,color:'#0078D4',lineHeight:1}}>{overview.total_scored_controls}</div>
            <div style={{fontSize:10,color:'#7A8899',marginTop:2}}>Controls scored</div>
          </div>
          <div className="stat-card" style={{padding:'10px 14px'}}>
            <div style={{fontSize:22,fontWeight:700,color:overview.pending + overview.running > 0 ? '#F57F17' : '#B0BAC5',lineHeight:1}}>
              {overview.pending + overview.running}
            </div>
            <div style={{fontSize:10,color:'#7A8899',marginTop:2}}>In queue</div>
          </div>
          <div className="stat-card" style={{padding:'10px 14px'}}>
            <div style={{fontSize:22,fontWeight:700,color:overview.failed + overview.blocked > 0 ? '#C62828' : '#B0BAC5',lineHeight:1}}>
              {overview.failed + overview.blocked}
            </div>
            <div style={{fontSize:10,color:'#7A8899',marginTop:2}}>Failed / Blocked</div>
          </div>
        </div>
      )}

      {/* Token telemetry - F4 */}
      {overview && (overview.total_prompt_tokens > 0 || overview.total_completion_tokens > 0) && (
        <div style={{
          display:'flex',gap:16,marginBottom:16,padding:'10px 14px',
          background:'#F8F9FA',borderRadius:8,border:'1px solid #E8ECF0',
          fontSize:10,color:'#7A8899',alignItems:'center'
        }}>
          <span>AI Usage:</span>
          <span><strong style={{color:'#455A64'}}>{((overview.total_prompt_tokens + overview.total_completion_tokens) / 1000).toFixed(1)}K</strong> tokens</span>
          <span><strong style={{color:'#455A64'}}>${overview.estimated_cost_usd}</strong> est. cost</span>
          {overview.completed > 0 && (
            <span><strong style={{color:'#455A64'}}>{(overview.total_latency_ms / overview.completed / 1000).toFixed(1)}s</strong> avg latency</span>
          )}
        </div>
      )}

      {/* Upload zone */}
      <div
        className="upload-zone"
        onDragOver={e => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
        style={{
          padding:'24px 16px',marginBottom:16,textAlign:'center',
          border: dragging ? '2px dashed #1565C0' : '2px dashed #E8ECF0',
          background: dragging ? '#E3F2FD' : '#FAFBFD',
          borderRadius:10,cursor:'pointer',transition:'all 0.2s'
        }}
        onClick={() => {
          const input = document.createElement('input');
          input.type = 'file';
          input.multiple = true;
          input.accept = '.pdf,.xlsx,.xls,.docx,.doc';
          input.onchange = (e) => handleUpload(Array.from(e.target.files), []);
          input.click();
        }}>
        <div style={{fontSize:13,fontWeight:500,color:'#1565C0',marginBottom:4}}>
          {dragging ? 'Drop files here' : 'Drag & drop files or click to browse'}
        </div>
        <div style={{fontSize:10,color:'#7A8899'}}>
          Supported: PDF, XLSX, DOCX (max 25 MB)
        </div>
      </div>

      {/* Domain filter */}
      <div style={{display:'flex',gap:6,marginBottom:12,flexWrap:'wrap'}}>
        <button
          className={`domain-tab${filterDomain==='all'?' active':''}`}
          style={{fontSize:10,padding:'3px 10px'}}
          onClick={() => setFilterDomain('all')}>
          All ({checklist.length})
        </button>
        {uniqueDomains.map(d => {
          const count = checklist.filter(c => c.domain === d).length;
          return (
            <button key={d}
              className={`domain-tab${filterDomain===d?' active':''}`}
              style={{fontSize:10,padding:'3px 10px'}}
              onClick={() => setFilterDomain(d)}>
              {d} ({count})
            </button>
          );
        })}
      </div>

      {/* Control checklist */}
      <div className="tbl-wrap">
        <table className="tbl">
          <thead>
            <tr>
              <th style={{width:80}}>Control</th>
              <th>Statement</th>
              <th style={{width:120}}>Evidence</th>
              <th style={{width:90}}>Status</th>
              <th style={{width:90}}>AI Score</th>
              <th style={{width:100}}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(c => {
              const isExpanded = expanded === c.id;
              const result = c.doc?.analysis?.results?.find(r => r.control_id === c.id);
              const statusStyle = STATUS_COLORS[c.doc?.processing_status] || STATUS_COLORS.Queued;

              return (
                <React.Fragment key={c.id}>
                <tr style={{cursor:'pointer'}} onClick={() => setExpanded(isExpanded ? null : c.id)}>
                  <td>
                    <span style={{fontSize:11,fontWeight:600,color:'#1565C0'}}>{c.id}</span>
                    <br/><span style={{fontSize:9,color:'#7A8899'}}>{c.domain}</span>
                  </td>
                  <td style={{fontSize:10,color:'#455A64'}}>{c.statement}</td>
                  <td style={{fontSize:10}}>
                    {c.doc ? (
                      <span style={{color:'#455A64'}} title={c.doc.file_name}>
                        {c.doc.file_name.length > 20 ? c.doc.file_name.slice(0,18) + '...' : c.doc.file_name}
                      </span>
                    ) : (
                      <span style={{color:'#B0BAC5',fontStyle:'italic'}}>No document</span>
                    )}
                  </td>
                  <td>
                    {c.doc ? (
                      <span className="pill" style={{
                        fontSize:9,
                        background: statusStyle.bg,
                        color: statusStyle.fg
                      }}>
                        {c.doc.processing_status}
                        {['Running','Processing'].includes(c.doc.processing_status) && (
                          <span style={{marginLeft:4,animation:'pulse 1.5s infinite'}}>...</span>
                        )}
                      </span>
                    ) : (
                      <span className="pill" style={{fontSize:9,background:'#FFF3E0',color:'#E65100'}}>Missing</span>
                    )}
                  </td>
                  <td>
                    {result ? (
                      <span style={{
                        fontSize:12,fontWeight:700,color:scoreColor(result.score)
                      }}>{result.score}/5</span>
                    ) : (
                      <span style={{fontSize:10,color:'#B0BAC5'}}>—</span>
                    )}
                  </td>
                  <td onClick={e => e.stopPropagation()}>
                    <div style={{display:'flex',gap:4}}>
                      {c.doc && (
                        <button
                          style={{fontSize:9,padding:'3px 8px',background:'#E3F2FD',color:'#1565C0',border:'1px solid #BBDEFB',borderRadius:4,cursor:'pointer'}}
                          onClick={() => setViewerDoc(c.doc)}>
                          View
                        </button>
                      )}
                      {!c.doc && (
                        <button
                          style={{fontSize:9,padding:'3px 8px',background:'#1565C0',color:'#fff',border:'none',borderRadius:4,cursor:'pointer'}}
                          onClick={() => {
                            const input = document.createElement('input');
                            input.type = 'file';
                            input.accept = '.pdf,.xlsx,.xls,.docx,.doc';
                            input.onchange = (ev) => handleUpload(
                              Array.from(ev.target.files),
                              [{ controlId: c.id, isPrimary: true }]
                            );
                            input.click();
                          }}>
                          Upload
                        </button>
                      )}
                    </div>
                  </td>
                </tr>

                {/* Expanded row with AI results */}
                {isExpanded && c.doc?.analysis?.status === 'Completed' && (
                  <tr>
                    <td colSpan={6} style={{background:'#F8F9FA',padding:'12px 16px'}}>
                      {c.doc.analysis.results.filter(r => (c.doc.mapped_controls || []).includes(r.control_id)).map(r => (
                        <div key={r.control_id} style={{marginBottom:12}}>
                          <div style={{display:'flex',gap:8,alignItems:'center',marginBottom:6}}>
                            <span style={{fontWeight:600,fontSize:11,color:'#1565C0'}}>{r.control_id}</span>
                            <span style={{
                              fontSize:10,fontWeight:600,color:'#fff',
                              background:scoreColor(r.score),
                              padding:'2px 8px',borderRadius:10
                            }}>Score: {r.score}</span>
                            <span className="pill" style={{fontSize:9,background:r.status==='provided'?'#E8F5E9':'#FFF3E0',color:r.status==='provided'?'#2E7D32':'#E65100'}}>
                              {r.status}
                            </span>
                          </div>
                          <div style={{fontSize:10,color:'#455A64',lineHeight:1.5,marginBottom:8}}>{r.rationale}</div>

                          {r.findings?.length > 0 && (
                            <div style={{marginBottom:8}}>
                              {r.findings.map((f, fi) => (
                                <div key={fi} style={{display:'flex',gap:6,alignItems:'flex-start',marginBottom:4,fontSize:10}}>
                                  <span style={{color:f.met?'#2E7D32':'#C62828',fontWeight:700}}>
                                    {f.met ? '✓' : '✗'}
                                  </span>
                                  <div>
                                    <span style={{color:'#455A64'}}>{f.requirement}</span>
                                    {f.citation && (
                                      <div style={{fontSize:9,color:'#1565C0',cursor:'pointer'}}
                                        onClick={() => { setViewerDoc(c.doc); }}>
                                        {f.citation}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}

                          {/* Confirm / Override */}
                          <div style={{display:'flex',gap:6,marginTop:8}}>
                            <button
                              style={{fontSize:9,padding:'4px 12px',background:'#2E7D32',color:'#fff',border:'none',borderRadius:4,cursor:'pointer'}}
                              onClick={() => handleConfirm(r.control_id)}>
                              Confirm score
                            </button>
                            <button
                              style={{fontSize:9,padding:'4px 12px',background:'#F57F17',color:'#fff',border:'none',borderRadius:4,cursor:'pointer'}}
                              onClick={() => { setOverrideOpen(r.control_id); setOverrideScore(r.score); }}>
                              Override
                            </button>
                          </div>

                          {overrideOpen === r.control_id && (
                            <div style={{marginTop:8,padding:'10px 12px',background:'#fff',border:'1px solid #E8ECF0',borderRadius:6}}>
                              <div style={{fontSize:10,fontWeight:500,marginBottom:6}}>Override score for {r.control_id}</div>
                              <div style={{display:'flex',gap:8,alignItems:'center',marginBottom:6}}>
                                <select value={overrideScore||''} onChange={e => setOverrideScore(parseInt(e.target.value))}
                                  style={{fontSize:10}}>
                                  <option value="1">1 — Initial</option>
                                  <option value="2">2 — Developing</option>
                                  <option value="3">3 — Defined</option>
                                  <option value="4">4 — Managed</option>
                                  <option value="5">5 — Optimized</option>
                                </select>
                              </div>
                              <textarea value={overrideReason} onChange={e => setOverrideReason(e.target.value)}
                                placeholder="Reason for override..."
                                style={{fontSize:10,width:'100%',minHeight:40,marginBottom:6}}/>
                              <div style={{display:'flex',gap:4}}>
                                <button className="btn-primary" style={{fontSize:9,padding:'4px 10px'}}
                                  onClick={() => handleOverride(r.control_id)}>Apply override</button>
                                <button className="btn-secondary" style={{fontSize:9,padding:'4px 10px'}}
                                  onClick={() => { setOverrideOpen(null); setOverrideScore(null); setOverrideReason(''); }}>Cancel</button>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </td>
                  </tr>
                )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Uploaded documents list */}
      {docs.length > 0 && (
        <div style={{marginTop:20}}>
          <div style={{fontSize:13,fontWeight:600,color:'#0D1B2A',marginBottom:10}}>Uploaded Documents</div>
          <div style={{display:'grid',gap:8}}>
            {docs.map(doc => {
              const statusStyle = STATUS_COLORS[doc.processing_status] || STATUS_COLORS.Queued;
              return (
                <div key={doc.id} style={{
                  padding:'10px 14px',background:'#fff',border:'1px solid #E8ECF0',
                  borderRadius:8,display:'flex',alignItems:'center',gap:12
                }}>
                  <div style={{
                    width:36,height:36,borderRadius:6,background:'#E3F2FD',
                    display:'flex',alignItems:'center',justifyContent:'center',
                    fontSize:10,fontWeight:600,color:'#1565C0'
                  }}>
                    {doc.mime_type?.includes('pdf') ? 'PDF' : doc.mime_type?.includes('sheet') || doc.mime_type?.includes('excel') ? 'XLS' : 'DOC'}
                  </div>
                  <div style={{flex:1}}>
                    <div style={{fontSize:11,fontWeight:500,color:'#0D1B2A'}}>{doc.file_name}</div>
                    <div style={{fontSize:9,color:'#7A8899'}}>
                      {formatSize(doc.size_bytes)} &middot; {formatDate(doc.uploaded_at)}
                      {doc.mapped_controls?.length > 0 && (
                        <span> &middot; {doc.mapped_controls.join(', ')}</span>
                      )}
                    </div>
                  </div>
                  <span className="pill" style={{fontSize:9,background:statusStyle.bg,color:statusStyle.fg}}>
                    {doc.processing_status}
                  </span>
                  <button
                    style={{fontSize:9,padding:'4px 10px',background:'#E3F2FD',color:'#1565C0',border:'1px solid #BBDEFB',borderRadius:4,cursor:'pointer'}}
                    onClick={() => setViewerDoc(doc)}>
                    View
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Document viewer modal */}
      {viewerDoc && (
        <DocumentViewer doc={viewerDoc} onClose={() => setViewerDoc(null)} />
      )}
    </div>
  );
}
